import { MutableRefObject, useEffect, useRef } from 'react';
import { BEAT, keywords, metrics, openingLines, REEL_DURATION, reelScenes } from '../data/reel';
import { createDubEngine, DubEngine, hat, impact, kick, riser, rim, stab } from '../utils/dubTechno';

const STEP = BEAT / 4;
const STEPS = Math.round(REEL_DURATION / STEP);
const toStep = (t: number) => Math.round(t / STEP);

const sceneStart = (id: string, fallback: number) => reelScenes.find((s) => s.id === id)?.start ?? fallback;

const dropTime = sceneStart('pencil', 5);
const DROP = toStep(dropTime);
const OUTRO = toStep(sceneStart('closing', 17.5));
const RISER_AT = dropTime - 1;

const keywordStart = sceneStart('keywords', 8);
const metricsStart = sceneStart('metrics', 12);

const cutSteps = new Set(reelScenes.map((s) => toStep(s.start)));
const accentSteps = new Set([
  ...keywords.map((_, i) => toStep(keywordStart + i * BEAT)),
  ...metrics.map((_, i) => toStep(metricsStart + i * BEAT)),
]);
const lineSteps = new Set(openingLines.map((l) => toStep(l.at)));

function playStep(e: DubEngine, step: number, at: number) {
  const inBar = step % 16;
  const groove = step >= DROP && step < OUTRO;

  if (groove && step % 4 === 0) kick(e, at, cutSteps.has(step) ? 1 : 0.85);
  if (step % 4 === 2 && step < STEPS - 4) hat(e, at, groove ? 1 : 0.6);
  if (groove && step >= toStep(8) && step % 2 === 1) hat(e, at, 0.3);

  if (step < OUTRO && (inBar === 3 || inBar === 10)) stab(e, at, groove ? 0.9 : 0.7);
  if (step >= OUTRO && inBar === 0 && step < STEPS - 8) stab(e, at, 1, 1);

  if (cutSteps.has(step) && step !== 0) impact(e, at, step === DROP ? 1 : 0.7);
  if (accentSteps.has(step)) rim(e, at);
  if (lineSteps.has(step)) stab(e, at, 0.8, 1);
  if (step === toStep(RISER_AT)) riser(e, at, dropTime - RISER_AT);
}

/** Plays a dub-techno bed locked to the reel clock, with hits on every cut. */
export function useDubTechno(timeRef: MutableRefObject<number>, playing: boolean, enabled: boolean) {
  const engineRef = useRef<DubEngine | null>(null);

  useEffect(() => {
    if (!enabled || !playing) return;
    if (!engineRef.current) {
      const ctx = new AudioContext();
      engineRef.current = createDubEngine(ctx, STEP);
    }
    const engine = engineRef.current;
    const { ctx } = engine;
    void ctx.resume();

    // Browsers block audio until the first interaction — unlock on any gesture.
    const unlock = () => void ctx.resume();
    const gestures = ['pointerdown', 'keydown', 'touchstart'] as const;
    if (ctx.state !== 'running') gestures.forEach((g) => window.addEventListener(g, unlock, { once: true }));
    engine.master.gain.cancelScheduledValues(ctx.currentTime);
    engine.master.gain.setTargetAtTime(0.85, ctx.currentTime, 0.05);

    let scheduledUntil = timeRef.current - 0.001;
    const id = window.setInterval(() => {
      const t = timeRef.current;
      if (t < scheduledUntil - 1) scheduledUntil = t; // looped or scrubbed back
      if (scheduledUntil < t - 0.2) scheduledUntil = t; // scrubbed forward
      const horizon = t + 0.12;
      for (let i = Math.floor(scheduledUntil / STEP) + 1; i * STEP <= horizon; i++) {
        const at = Math.max(ctx.currentTime, ctx.currentTime + (i * STEP - t));
        playStep(engine, i % STEPS, at);
      }
      scheduledUntil = horizon;
    }, 25);

    return () => {
      window.clearInterval(id);
      gestures.forEach((g) => window.removeEventListener(g, unlock));
      engine.master.gain.cancelScheduledValues(ctx.currentTime);
      engine.master.gain.setTargetAtTime(0, ctx.currentTime, 0.04);
    };
  }, [enabled, playing, timeRef]);

  useEffect(
    () => () => {
      void engineRef.current?.ctx.close();
    },
    [],
  );
}
